import React, { ChangeEvent, DragEvent, useMemo, useRef, useState } from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { Alert, Box, Button, Grid, Stack, Typography } from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';

import type { CppPartner, PartnerEarnCsvRow } from '~/services/LuxLoyalty';

import { formatDecimal } from '~/utils/numberFormatters';

import { downloadPartnerEarnTemplate, parsePartnerEarnCsv, validatePartnerEarnCsvRows } from '../csvHelpers';
import { partnerLimitsLabel } from '../partnerEarnCredit';

interface Props {
  partner: CppPartner;
  onBack: () => void;
  onProceedToReview: (rows: Array<PartnerEarnCsvRow>) => void;
}

export default function BulkUploadStep({ partner, onBack, onProceedToReview }: Props) {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [parsedRows, setParsedRows] = useState<Array<PartnerEarnCsvRow>>([]);
  const [parseErrors, setParseErrors] = useState<Array<string>>([]);
  const [parseWarnings, setParseWarnings] = useState<Array<string>>([]);
  const [fileName, setFileName] = useState('');
  const [isDragOver, setIsDragOver] = useState(false);

  const columns: Array<GridColDef<PartnerEarnCsvRow>> = useMemo(
    () => [
      { field: 'accountNumber', headerName: 'ACCOUNT NUMBER', flex: 1 },
      { field: 'basePoints', headerName: 'BASE POINTS', flex: 1 },
      { field: 'statusCredits', headerName: 'STATUS CREDITS', flex: 1 },
      { field: 'activityTitle', headerName: 'ACTIVITY TITLE', flex: 1.5 },
      { field: 'reason', headerName: 'REASON', flex: 1.5 },
      { field: 'sourceKey', headerName: 'SOURCE KEY', flex: 1 },
    ],
    [],
  );

  const processFile = (file: File) => {
    setParseErrors([]);
    setParseWarnings([]);
    setParsedRows([]);
    setFileName(file.name);

    if (file.size > 25 * 1024 * 1024) {
      setParseErrors(['File exceeds maximum size of 25MB']);
      return;
    }

    const reader = new FileReader();
    reader.onload = (evt) => {
      if (!evt?.target?.result || typeof evt.target.result !== 'string') {
        return;
      }

      const { data, parseErrors: csvParseErrors } = parsePartnerEarnCsv(evt.target.result);
      if (csvParseErrors.length > 0) {
        setParseErrors(csvParseErrors);
        return;
      }

      const { rows, errors, warnings } = validatePartnerEarnCsvRows(data, partner);
      setParseWarnings(warnings);
      if (errors.length > 0) {
        setParseErrors(errors);
        return;
      }

      setParsedRows(rows);
    };
    reader.readAsText(file);
  };

  const handleFileUpload = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    processFile(file);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (!file || !file.name.endsWith('.csv')) {
      setParseErrors(['Please upload a CSV file']);
      return;
    }
    processFile(file);
  };

  const canProceed = parsedRows.length > 0;

  return (
    <Box>
      <Grid container spacing={3} columns={12}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Stack spacing={3}>
            <Box
              onDrop={handleDrop}
              onDragOver={(e) => {
                e.preventDefault();
                setIsDragOver(true);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                setIsDragOver(false);
              }}
              sx={{
                border: 2,
                borderStyle: 'dashed',
                borderColor: isDragOver ? 'primary.main' : 'divider',
                bgcolor: isDragOver ? 'action.hover' : 'transparent',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                transition: 'all 0.15s ease',
              }}
            >
              <UploadFileIcon color="primary" sx={{ fontSize: 48, mb: 1 }} />
              <Typography variant="subtitle1" gutterBottom>
                Upload CSV
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={2}>
                Drag and drop a partner earn CSV or browse files. Maximum 500 rows.
              </Typography>
              <Button variant="contained" component="label">
                Browse Files
                <input ref={fileInputRef} type="file" hidden accept=".csv" onChange={handleFileUpload} />
              </Button>
            </Box>

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected partner
              </Typography>
              <Typography variant="body2">{partner.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {partner.partnerType} · {partnerLimitsLabel(partner)}
              </Typography>
            </Box>

            <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                Need the template?
              </Typography>
              <Typography variant="body2" color="text.secondary" mb={1}>
                Columns: account_number, base_points, status_credits, activity_title, reason, source_key
              </Typography>
              <Button size="small" startIcon={<DownloadIcon />} onClick={downloadPartnerEarnTemplate}>
                Download CSV Template
              </Button>
            </Box>

            {parsedRows.length > 0 && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  File: {fileName}
                </Typography>
                <Typography variant="body2">
                  {formatDecimal(parsedRows.length)} row{parsedRows.length !== 1 ? 's' : ''} ready to process
                </Typography>
              </Box>
            )}
          </Stack>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          {parseErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                CSV Validation Errors:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {parseErrors.slice(0, 10).map((error) => (
                  <li key={error}>{error}</li>
                ))}
                {parseErrors.length > 10 && <li>...and {parseErrors.length - 10} more</li>}
              </ul>
            </Alert>
          )}

          {parseWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                CSV Warnings:
              </Typography>
              <ul style={{ margin: 0, paddingLeft: 20 }}>
                {parseWarnings.slice(0, 10).map((warning) => (
                  <li key={warning}>{warning}</li>
                ))}
                {parseWarnings.length > 10 && <li>...and {parseWarnings.length - 10} more</li>}
              </ul>
            </Alert>
          )}

          {parsedRows.length > 0 && (
            <>
              <Typography variant="subtitle1" mb={1}>
                Data Preview{' '}
                <Typography component="span" variant="body2" color="text.secondary">
                  {parsedRows.length} ROWS
                </Typography>
              </Typography>
              <DataGrid
                rows={parsedRows}
                columns={columns}
                getRowId={(row) => `${row.rowIndex}-${row.accountNumber}`}
                initialState={{ pagination: { paginationModel: { pageSize: 50 } } }}
                pageSizeOptions={[25, 50, 100]}
                autoHeight
                disableRowSelectionOnClick
              />
            </>
          )}

          {parsedRows.length === 0 && parseErrors.length === 0 && (
            <Box
              display="flex"
              justifyContent="center"
              alignItems="center"
              minHeight={300}
              sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}
            >
              <Typography color="text.secondary">Upload a CSV file to preview data</Typography>
            </Box>
          )}
        </Grid>
      </Grid>

      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mt={3}
        pt={2}
        borderTop={1}
        borderColor="divider"
      >
        <Box>
          {parsedRows.length > 0 && (
            <Typography variant="body2" color="success.main">
              {parsedRows.length} row{parsedRows.length !== 1 ? 's' : ''} will be processed
            </Typography>
          )}
        </Box>
        <Stack direction="row" spacing={2}>
          <Button variant="text" onClick={onBack}>
            Back
          </Button>
          <Button variant="contained" onClick={() => onProceedToReview(parsedRows)} disabled={!canProceed}>
            Proceed to Review
          </Button>
        </Stack>
      </Stack>
    </Box>
  );
}
